import type {
  AnalyticsCockpitLayout,
  AnalyticsDashboard,
  AnalyticsDashboardInput,
  AnalyticsGoal,
  AnalyticsGoalInput,
  AnalyticsPrefs,
  AnalyticsPrefsInput,
  AnalyticsQueryFilters,
  AnalyticsQueryRequest,
  AnalyticsQueryResult,
  AnalyticsReport,
  AnalyticsReportInput,
  AnalyticsSummary,
  AnalyticsCatalogDashboard,
  AnalyticsCatalogReport,
  AnalyticsGroupBy,
} from "@sales-ops-amplify/shared";
import { apiGet, apiPatch, apiPost, apiPut } from "./client";

export async function getAnalyticsSummary(): Promise<AnalyticsSummary> {
  return apiGet<AnalyticsSummary>("/api/analytics/summary");
}

export function queryAnalytics(
  body: AnalyticsQueryRequest,
): Promise<AnalyticsQueryResult> {
  return apiPost("/api/analytics/query", body);
}

export function getAnalyticsCatalog(): Promise<{
  dashboards: AnalyticsCatalogDashboard[];
  reports: AnalyticsCatalogReport[];
}> {
  return apiGet("/api/analytics/catalog");
}

export function getAnalyticsPreferences(): Promise<AnalyticsPrefs> {
  return apiGet("/api/analytics/preferences");
}

export function updateAnalyticsPreferences(
  input: AnalyticsPrefsInput,
): Promise<AnalyticsPrefs> {
  return apiPut("/api/analytics/preferences", input);
}

export function listAnalyticsDashboards(): Promise<{
  catalog: AnalyticsCatalogDashboard[];
  items: AnalyticsDashboard[];
}> {
  return apiGet("/api/analytics/dashboards");
}

export function getAnalyticsDashboard(id: string): Promise<{
  dashboard: AnalyticsDashboard | (AnalyticsCatalogDashboard & {
    dashboardId: string;
    source: "apollo_prebuilt";
  });
  filters?: AnalyticsQueryFilters;
}> {
  return apiGet(`/api/analytics/dashboards/${encodeURIComponent(id)}`);
}

export function createAnalyticsDashboard(
  input: AnalyticsDashboardInput,
): Promise<AnalyticsDashboard> {
  return apiPost("/api/analytics/dashboards", input);
}

export function updateAnalyticsDashboard(
  id: string,
  patch: Partial<AnalyticsDashboardInput> & { favorite?: boolean },
): Promise<AnalyticsDashboard> {
  return apiPatch(`/api/analytics/dashboards/${encodeURIComponent(id)}`, patch);
}

export function setDefaultAnalyticsDashboard(
  id: string,
): Promise<AnalyticsPrefs> {
  return apiPost(
    `/api/analytics/dashboards/${encodeURIComponent(id)}/default`,
    {},
  );
}

export function listAnalyticsReports(): Promise<{
  catalog: AnalyticsCatalogReport[];
  items: AnalyticsReport[];
}> {
  return apiGet("/api/analytics/reports");
}

export function getAnalyticsReport(
  id: string,
): Promise<
  | AnalyticsReport
  | (AnalyticsCatalogReport & { reportId: string; source: "apollo_prebuilt" })
> {
  return apiGet(`/api/analytics/reports/${encodeURIComponent(id)}`);
}

export function createAnalyticsReport(
  input: AnalyticsReportInput,
): Promise<AnalyticsReport> {
  return apiPost("/api/analytics/reports", input);
}

export function runAnalyticsReport(
  id: string,
  body?: { filters?: AnalyticsQueryFilters; groupBy?: AnalyticsGroupBy },
): Promise<{
  report:
    | AnalyticsReport
    | (AnalyticsCatalogReport & {
        reportId: string;
        source: "apollo_prebuilt";
      });
  result: AnalyticsQueryResult;
}> {
  return apiPost(
    `/api/analytics/reports/${encodeURIComponent(id)}/run`,
    body ?? {},
  );
}

export function listAnalyticsGoals(): Promise<{ goals: AnalyticsGoal[] }> {
  return apiGet("/api/analytics/goals");
}

export function createAnalyticsGoal(
  input: AnalyticsGoalInput,
): Promise<AnalyticsGoal> {
  return apiPost("/api/analytics/goals", input);
}

export function updateAnalyticsGoal(
  id: string,
  patch: Partial<AnalyticsGoalInput>,
): Promise<AnalyticsGoal> {
  return apiPatch(`/api/analytics/goals/${encodeURIComponent(id)}`, patch);
}

export function getAnalyticsCockpit(layout?: AnalyticsCockpitLayout): Promise<{
  layout: AnalyticsCockpitLayout;
  week: AnalyticsQueryResult;
  sequences: Array<{
    id: string;
    name: string;
    enrollments: number;
    replies: number;
  }>;
  tasksOpen: number;
}> {
  return apiGet(
    "/api/analytics/cockpit",
    layout ? { layout } : undefined,
  );
}

export function postAnalyticsAiInsights(prompt: string): Promise<{
  answer: string;
  result?: AnalyticsQueryResult;
}> {
  return apiPost("/api/analytics/ai-insights", { prompt });
}
